import { getLang , type TitleBlock } from '@enercad/core';
import type { AiOp, BoardSpec, CustomSymbol, Front, FrontVector, Macro, MacroSummary, Part, Project } from '@enercad/core';
import { t as $t } from '@enercad/core';


export interface User {
  id: number;
  email: string;
  name: string;
  /** role inside the company */
  role: 'admin' | 'user';
  tenant_id: number;
  platform_admin: number;
}
export interface TenantInfo {
  id: number;
  name: string;
  plan: 'trial' | 'basic' | 'pro' | 'enterprise';
  planName: string;
  status: 'active' | 'suspended';
  expires: string | null;
  daysLeft: number | null;
  readOnly: boolean;
  limits: { users: number | null; projects: number | null };
  usage: { users: number; projects: number };
}
export interface PlatformTenant {
  id: number;
  name: string;
  plan: TenantInfo['plan'];
  status: 'active' | 'suspended';
  trial_ends: string | null;
  paid_until: string | null;
  max_users: number | null;
  max_projects: number | null;
  notes: string | null;
  created_at: string;
  users: number;
  projects: number;
  bytes: number;
  admin_email: string | null;
  last_activity: string | null;
  info: TenantInfo;
}

export interface PlatformUser {
  id: number;
  email: string;
  name: string;
  role: 'admin' | 'user';
  disabled: number;
  platform_admin: number;
  email_verified: number;
  created_at: string;
  last_login: string | null;
}

/** Library scope for the parts page: the company's own view, or the shared library (platform admin). */
let libraryScope: 'tenant' | 'shared' = 'tenant';
export const setLibraryScope = (s: 'tenant' | 'shared') => (libraryScope = s);
export const getLibraryScope = () => libraryScope;
export type ProjectRole = 'owner' | 'write' | 'read';
export interface ProjectSummary {
  id: string;
  name: string;
  version: number;
  updated_at: string;
  pages: number;
  owner: string;
  role: ProjectRole;
  scope: 'mine' | 'shared' | 'others';
  updated_by: string | null;
  is_template: number;
}
export interface LiveState {
  version: number;
  by?: string;
  /** other open editors of the project */
  users: { name: string; page: string; me: boolean }[];
  /** page id → name of the editor holding its lock (others only) */
  locks: Record<string, string>;
  /** pages this editor holds */
  mine: string[];
}
export interface AiMotor {
  tag: string;
  desc: string;
  kw: number;
  kind: 'dol' | 'rev' | 'yd' | 'vfd' | 'ss';
  control: 'plc' | 'local' | 'auto' | 'autoplc';
  cableLen: number;
  speedRef?: boolean;
  ptc?: boolean;
}
export interface AiIo {
  tag: string;
  desc: string;
  kind: 'switch_no' | 'switch_nc' | 'lamp' | 'valve' | 'analog' | 'valve_prop';
}
export interface AiDist {
  name: string;
  incomer: { kind: 'lbs' | 'mccb' | 'acb'; amps: number; meter: boolean; spd: boolean; ats: boolean; manual?: boolean; comp: number[] };
  feeders: { tag: string; desc: string; amps: number; poles: 1 | 3; rcd: boolean; cableLen?: number; fuse?: boolean; meter?: boolean }[];
  assumptions: string[];
  questions: string[];
}
export interface AiPlan {
  name: string;
  motors: AiMotor[];
  io: AiIo[];
  /** emergency stop buttons of the template e-stop circuit */
  estop: number;
  assumptions: string[];
  questions: string[];
}
export interface AiFinding {
  severity: 'error' | 'warning' | 'suggestion';
  tag: string;
  page: number;
  title: string;
  detail: string;
}
export interface AiReview {
  summary: string;
  findings: AiFinding[];
}
export interface AiFix {
  explanation: string;
  ops: AiOp[];
  manual: string[];
}
export interface AiProviderView {
  id: string;
  name: string;
  /** default API address; empty when the user gives it */
  url: string;
  models: string[];
  pdf: boolean;
  keyOptional?: boolean;
  keyFrom?: string;
}
export interface AiSettings {
  enabled: boolean;
  provider: string;
  baseUrl: string;
  model: string;
  /** requests per month; 0 = no limit */
  monthlyLimit: number;
}
/** The company's AI account (company admin); the key itself never comes back, only its last 4 characters. */
export interface TenantAiView {
  settings: AiSettings;
  keySet: boolean;
  keyHint: string;
  providers: AiProviderView[];
  used: number;
}
export interface MailSettings {
  host: string;
  port: number;
  secure: boolean;
  user: string;
  from: string;
  publicUrl: string;
  verify: boolean;
}
export interface MailView {
  settings: MailSettings;
  passSet: boolean;
  source: 'db' | 'env' | 'none';
}
export type PaidPlan = 'basic' | 'pro' | 'enterprise';
export type Currency = 'TRY' | 'EUR';
export type Period = 'month' | 'year';
export type Prices = Record<Period, Partial<Record<Currency, number>>>;
export interface BillingInfo {
  type: 'individual' | 'company';
  abroad: boolean;
  country: string;
  name: string;
  idNo: string;
  taxOffice: string;
  taxNo: string;
  vatId: string;
  address: string;
  email: string;
}
export interface PaymentRequest {
  id: number;
  ref: string;
  tenant_id: number;
  plan: PaidPlan;
  period: Period;
  currency: Currency;
  amount: number;
  status: 'open' | 'paid' | 'cancelled';
  created_at: string;
  closed_at: string | null;
  paid_until: string | null;
  billing: BillingInfo;
  tenant_name?: string;
  user_email?: string | null;
  user_name?: string | null;
}
export interface BillingView {
  plans: { key: PaidPlan; name: string; users: number | null; projects: number | null; prices: Prices }[];
  bank: string;
  note: string;
  tenant: TenantInfo;
  open: PaymentRequest | null;
  billing: BillingInfo | null;
}
export interface BillingSettings {
  prices: Record<PaidPlan, Prices>;
  bank: string;
  note: string;
}
export interface ApiKey {
  id: number;
  name: string;
  prefix: string;
  owner: string;
  created_at: string;
  last_used: string | null;
  revoked_at: string | null;
}
export interface RestoreResult {
  projects: number;
  parts: number;
  fronts: number;
  symbols: number;
  macros: number;
}
export interface ProjectDoc {
  id: string;
  name: string;
  version: number;
  updated_at: string;
  role: ProjectRole;
  data: Project;
  /** a local project: kept on the user's computer, never stored on the server */
  local?: boolean;
}
export interface Member {
  id: number;
  name: string;
  email: string;
  role: 'read' | 'write';
}
export interface VersionInfo {
  id: number;
  version: number;
  kind: 'auto' | 'rev';
  label: string | null;
  note: string | null;
  created_at: string;
  author: string | null;
}
export interface AdminUser {
  id: number;
  email: string;
  name: string;
  role: 'admin' | 'user';
  disabled: number;
  created_at: string;
  /** last request with a session (UTC, SQL format), at most a minute old while active */
  last_seen: string | null;
  projects: number;
}

export class ApiError extends Error {
  constructor(public status: number, message: string, public body: any = {}) {
    super(message);
  }
}

async function req<T>(method: string, url: string, body?: unknown): Promise<T> {
  let res: Response;
  if (libraryScope === 'shared' && url.startsWith('/parts')) url += (url.includes('?') ? '&' : '?') + 'scope=shared';
  const binary = body instanceof Blob;
  try {
    res = await fetch('/api' + url, {
      method,
      credentials: 'same-origin',
      headers: { 'content-type': binary ? 'application/octet-stream' : 'application/json', 'x-enercad': '1', 'x-lang': getLang() },
      body: body === undefined ? undefined : binary ? body : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, $t("Sunucuya ulaşılamadı. Bağlantınızı kontrol edin."));
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(res.status, data.error || $t("Sunucu hatası ({0})", res.status), data);
  return data as T;
}

/** DWG → DXF text, converted on the server (the browser cannot read DWG). */
async function dwgToDxf(file: Blob): Promise<string> {
  let res: Response;
  try {
    res = await fetch('/api/import/dwg', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/octet-stream', 'x-enercad': '1', 'x-lang': getLang() },
      body: file,
    });
  } catch {
    throw new ApiError(0, $t("Sunucuya ulaşılamadı. Bağlantınızı kontrol edin."));
  }
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new ApiError(res.status, data.error || $t("Sunucu hatası ({0})", res.status), data);
  }
  return res.text();
}

export const api = {
  dwgToDxf,
  me: () => req<{ user: User | null; tenant: TenantInfo | null; canRegister: boolean; signup: boolean; firstRun: boolean; verifyPending: boolean; canResetPassword: boolean }>('GET', '/auth/me'),
  forgotPassword: (email: string) => req('POST', '/auth/forgot', { email }),
  resetPassword: (token: string, password: string) => req('POST', '/auth/reset', { token, password }),
  verifyEmail: (token: string) => req('POST', '/auth/verify', { token }),
  resendVerify: () => req('POST', '/auth/verify/resend'),
  signup: (company: string, name: string, email: string, password: string) => req('POST', '/auth/signup', { company, name, email, password }),
  setupPlatform: (company: string, name: string, email: string, password: string) => req('POST', '/auth/register', { company, name, email, password }),
  renameTenant: (name: string) => req<{ tenant: TenantInfo }>('PATCH', '/tenant', { name }),
  aiStatus: () => req<{ available: boolean; used?: number; limit?: number; pdf?: boolean }>('GET', '/ai/status'),
  tenantAi: () => req<TenantAiView>('GET', '/tenant/ai'),
  saveTenantAi: (body: AiSettings & { apiKey?: string }) => req<TenantAiView>('PUT', '/tenant/ai', body),
  tenantAiModels: (body: { provider: string; baseUrl: string; apiKey?: string }) => req<{ models: { id: string; name?: string }[] }>('POST', '/tenant/ai/models', body),
  testTenantAi: () => req<{ ok: boolean; ms: number; msg: string }>('POST', '/tenant/ai/test'),
  aiDistribution: (prompt: string) => req<AiDist>('POST', '/ai/distribution', { prompt, lang: getLang() }),
  generateDistribution: (id: string, incomer: AiDist['incomer'], feeders: AiDist['feeders'], layout = false) =>
    req<{ version: number; pages: number; warnings: string[] }>('POST', `/projects/${encodeURIComponent(id)}/distribution`, { incomer, feeders, layout, replaceEmpty: true }),
  aiBoard: (note: string, file?: { name: string; type: string; data: string }) =>
    req<{ spec: BoardSpec; assumptions: string[]; questions: string[] }>('POST', '/ai/board', { note, ...(file ? { file } : {}), lang: getLang() }),
  aiSingleLine: (file: { name: string; type: string; data: string }, note: string) => req<AiDist>('POST', '/ai/singleline', { file, note, lang: getLang() }),
  aiMotors: (prompt: string) => req<AiPlan>('POST', '/ai/motors', { prompt, lang: getLang() }),
  generateMotors: (id: string, motors: AiMotor[], io: AiIo[] = [], estop = 0) => req<{ version: number; pages: number; warnings: string[] }>('POST', `/projects/${encodeURIComponent(id)}/motors`, { motors, io, estop, replaceEmpty: true }),
  aiFix: (project: string, finding: AiFinding) => req<AiFix>('POST', '/ai/fix', { project, finding, lang: getLang() }),
  aiReview: (project: string) => req<AiReview>('POST', '/ai/review', { project, lang: getLang() }),
  mailSettings: () => req<MailView>('GET', '/platform/mail'),
  saveMailSettings: (body: MailSettings & { pass?: string }) => req<MailView>('PUT', '/platform/mail', body),
  testMail: () => req<{ ok: boolean; ms: number; to?: string; msg?: string }>('POST', '/platform/mail/test'),
  billing: () => req<BillingView>('GET', '/billing'),
  billingRequest: (b: { plan: PaidPlan; period: Period; currency: Currency; billing: Partial<BillingInfo> }) =>
    req<{ request: PaymentRequest; bank: string; note: string }>('POST', '/billing/request', b),
  cancelBillingRequest: (id: number) => req('DELETE', `/billing/request/${id}`),
  platformBilling: () => req<{ settings: BillingSettings }>('GET', '/platform/billing'),
  savePlatformBilling: (s: BillingSettings) => req<{ settings: BillingSettings }>('PUT', '/platform/billing', s),
  platformPayments: () => req<{ requests: PaymentRequest[] }>('GET', '/platform/payments'),
  platformPaid: (id: number) => req<{ paid_until: string }>('POST', `/platform/payments/${id}/paid`),
  platformCancelPayment: (id: number) => req('POST', `/platform/payments/${id}/cancel`),
  platformTenants: () => req<{ tenants: PlatformTenant[] }>('GET', '/platform/tenants'),
  platformPatch: (id: number, body: Record<string, unknown>) => req('PATCH', `/platform/tenants/${id}`, body),
  platformUsers: (id: number) => req<{ users: PlatformUser[] }>('GET', `/platform/tenants/${id}/users`),
  platformPatchUser: (uid: number, b: { email?: string; name?: string; email_verified?: true }) => req('PATCH', `/platform/users/${uid}`, b),
  platformResetPassword: (uid: number, password: string) => req('POST', `/platform/users/${uid}/password`, { password }),
  platformCreate: (b: { company: string; name: string; email: string; password: string; plan: string; paid_until: string | null }) => req('POST', '/platform/tenants', b),
  login: (email: string, password: string) => req<{ user: User }>('POST', '/auth/login', { email, password }),
  register: (email: string, name: string, password: string) => req<{ user: User }>('POST', '/auth/register', { email, name, password }),
  logout: () => req('POST', '/auth/logout'),
  list: () => req<{ projects: ProjectSummary[] }>('GET', '/projects'),
  dictionary: (lang: string) => req<{ lang: string; entries: Record<string, string> }>('GET', `/dictionary?lang=${lang}`),
  saveDictionary: (lang: string, entries: Record<string, string>) => req<{ saved: number }>('PUT', '/dictionary', { lang, entries }),
  create: (body: { template?: string; name?: string; data?: unknown }) =>
    req<{ id: string; name: string; version: number }>('POST', '/projects', body),
  get: (id: string) => req<ProjectDoc>('GET', `/projects/${encodeURIComponent(id)}`),
  save: (id: string, data: Project, version: number, client?: string) => req<{ version: number }>('PUT', `/projects/${encodeURIComponent(id)}`, { data, version, client }),
  live: (id: string, body: { client: string; page?: string; edit?: string[]; leave?: boolean }) => req<LiveState>('POST', `/projects/${encodeURIComponent(id)}/live`, body),
  remove: (id: string) => req('DELETE', `/projects/${encodeURIComponent(id)}`),
  setTemplate: (id: string, on: boolean) => req('PATCH', `/projects/${encodeURIComponent(id)}/template`, { on }),
  restore: (backup: unknown) => req<RestoreResult>('POST', '/tenant/restore', backup),
  startUpload: (name: string, size: number) => req<{ id: string; chunk: number }>('POST', '/parts/upload', { name, size }),
  uploadChunk: (id: string, offset: number, chunk: Blob) => req<{ got: number }>('PUT', `/parts/upload/${id}?offset=${offset}`, chunk),
  startUploadImport: (id: string, replace: boolean) => req('POST', `/parts/upload/${id}/import`, { replace }),
  uploadStatus: (id: string) => req<{ state: 'uploading' | 'running' | 'done' | 'error'; got: number; size: number; rows: number; result?: ImportResult; error?: string }>('GET', `/parts/upload/${id}`),
  cancelUpload: (id: string) => req('DELETE', `/parts/upload/${id}`),
  partModels: (pns: string[]) => req<{ models: Record<string, { perm: [number, number, number]; flip?: boolean } | null> }>('GET', `/parts/models?pn=${encodeURIComponent(pns.join(','))}`),
  savePartModel: (pn: string, glb: Blob, orient?: { perm: [number, number, number]; flip?: boolean }) =>
    req<{ ok: boolean; size: number }>('PUT', `/parts/${encodeURIComponent(pn)}/model${orient ? `?orient=${encodeURIComponent(JSON.stringify(orient))}` : ''}`, glb),
  setPartModelOrient: (pn: string, orient: { perm: [number, number, number]; flip?: boolean }) => req('PATCH', `/parts/${encodeURIComponent(pn)}/model`, { orient }),
  deletePartModel: (pn: string) => req('DELETE', `/parts/${encodeURIComponent(pn)}/model`),
  companyFrame: () => req<{ frame: TitleBlock | null }>('GET', '/tenant/frame'),
  saveCompanyFrame: (frame: TitleBlock | null) => req<{ frame: TitleBlock | null }>('PUT', '/tenant/frame', { frame }),
  apiKeys: () => req<{ keys: ApiKey[] }>('GET', '/api-keys'),
  createApiKey: (name: string) => req<{ id: number; key: string }>('POST', '/api-keys', { name }),
  revokeApiKey: (id: number) => req('DELETE', `/api-keys/${id}`),
  members: (id: string) => req<{ owner: { id: number; name: string; email: string }; members: Member[]; role: ProjectRole; candidates: { id: number; name: string; email: string }[] }>('GET', `/projects/${encodeURIComponent(id)}/members`),
  share: (id: string, email: string, role: 'read' | 'write') => req('POST', `/projects/${encodeURIComponent(id)}/members`, { email, role }),
  unshare: (id: string, uid: number) => req('DELETE', `/projects/${encodeURIComponent(id)}/members/${uid}`),
  versions: (id: string) => req<{ versions: VersionInfo[]; current: number }>('GET', `/projects/${encodeURIComponent(id)}/versions`),
  versionData: (id: string, vid: number) => req<VersionInfo & { data: Project }>('GET', `/projects/${encodeURIComponent(id)}/versions/${vid}`),
  createRevision: (id: string, label: string, note: string, version: number) =>
    req<{ version: number; revision: NonNullable<Project['revision']> }>('POST', `/projects/${encodeURIComponent(id)}/versions`, { label, note, version }),
  restoreVersion: (id: string, vid: number) => req<{ version: number }>('POST', `/projects/${encodeURIComponent(id)}/versions/${vid}/restore`, {}),
  changePassword: (current: string, next: string) => req('POST', '/auth/password', { current, next }),
  adminUsers: () => req<{ users: AdminUser[]; tenant?: TenantInfo }>('GET', '/admin/users'),
  adminCreate: (u: { email: string; name: string; password: string; role: 'admin' | 'user' }) => req('POST', '/admin/users', u),
  adminPatch: (id: number, b: { role?: 'admin' | 'user'; disabled?: boolean; password?: string; name?: string; email?: string }) => req('PATCH', `/admin/users/${id}`, b),
  parts: (q: string, type = '', limit = 100) =>
    req<{ parts: Part[]; total: number }>('GET', `/parts?q=${encodeURIComponent(q)}&type=${encodeURIComponent(type)}&limit=${limit}`),
  lookupParts: (pns: string[]) => req<{ parts: Part[] }>('GET', `/parts/lookup?pn=${encodeURIComponent(pns.join(','))}`),
  savePart: (p: Part) => req<{ part: Part }>('POST', '/parts', p),
  fronts: (pns: string[]) => req<{ fronts: Record<string, Front> }>('GET', `/parts/fronts?pn=${encodeURIComponent(pns.join(','))}`),
  putFront: (pn: string, body: FrontVector | Blob, setSize = false) =>
    req<{ front: { kind?: string; w: number; h: number } }>('PUT', `/parts/${encodeURIComponent(pn)}/front${setSize ? '?setSize=1' : ''}`, body),
  deleteFront: (pn: string) => req('DELETE', `/parts/${encodeURIComponent(pn)}/front`),
  deletePart: (pn: string) => req('DELETE', `/parts/${encodeURIComponent(pn)}`),
  symbols: () => req<{ symbols: (CustomSymbol & { createdBy: number; shared: boolean; editable: boolean })[] }>('GET', '/symbols'),
  saveSymbol: (s: Partial<CustomSymbol> & { shared?: boolean }) => req<{ symbol: CustomSymbol }>('POST', '/symbols', s),
  deleteSymbol: (key: string) => req('DELETE', `/symbols/${encodeURIComponent(key)}`),
  macros: () => req<{ macros: (MacroSummary & { mine?: boolean; shared?: boolean })[] }>('GET', '/macros'),
  saveMacro: (m: Macro & { shared?: boolean }) => req<{ id: string; name: string }>('POST', '/macros', m),
  deleteMacro: (id: string) => req('DELETE', `/macros/${encodeURIComponent(id)}`),
  /** rows of an Excel (.xlsx) or CSV file, read on the server */
  sheetRows: (file: File) => req<{ rows: string[][] }>('POST', `/sheet-rows?format=${/\.xlsx$/i.test(file.name) ? 'xlsx' : 'csv'}`, file),
  importParts: (file: File, replace: boolean) =>
    req<ImportResult>('POST', `/parts/import?format=${/\.xlsx$/i.test(file.name) ? 'xlsx' : /\.xml$/i.test(file.name) ? 'xml' : 'csv'}&replace=${replace ? 1 : 0}`, file),
};

export interface ImportResult {
  inserted: number;
  updated: number;
  skipped: number;
  columns: Record<string, string>;
  errors: { row: number; msg: string }[];
  format?: string;
}
